import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

import { Button, type ButtonProps } from "./Button";
import { Tooltip } from "./Tooltip";

export interface IconButtonProps
  extends Omit<ButtonProps, "children" | "size" | "aria-label"> {
  /**
   * Required. The product reference ships icon-only controls with no accessible
   * name; making the label mandatory in the type system means that defect
   * cannot be reintroduced here — an unlabelled icon button will not compile.
   */
  label: string;
  icon: ReactNode;
  /** Set false for controls whose label is already visible nearby. */
  showTooltip?: boolean;
}

export function IconButton({
  label,
  icon,
  showTooltip = true,
  className,
  variant = "ghost",
  ...props
}: IconButtonProps) {
  const button = (
    <Button
      aria-label={label}
      variant={variant}
      size="icon"
      className={cn("shrink-0", className)}
      {...props}
    >
      <span aria-hidden="true" className="flex items-center justify-center">
        {icon}
      </span>
    </Button>
  );

  if (!showTooltip) {
    return button;
  }

  return <Tooltip content={label}>{button}</Tooltip>;
}
